import {
    getHours,
    getMilliseconds,
    getMinutes,
    getSeconds,
    getTimestamp,
} from '../../owaDate/getFields';
import getIntlDate from './getIntlDate';
import getIntlDay from './getIntlDay';
import getIntlMonth from './getIntlMonth';
import getIntlYear from './getIntlYear';
import type { OwaDate } from '../../schema';
import { GlobalCalendarLocale } from '../../intl/globalCalendarLocale';
import formatDateStrings from '../formatDateStrings';

/** Formats a date according to an OWA-style format string. */
export default function formatIntlDate(
    date: OwaDate,
    locale: GlobalCalendarLocale,
    format: string,
    strings = formatDateStrings()
) {
    if (date == void 0) {
        throw TypeError(`Invalid Date (${date})`);
    }

    if (isNaN(getTimestamp(date))) {
        throw TypeError('Invalid Date (NaN)');
    }

    if (format == void 0) {
        throw TypeError(`Invalid Format (${format})`);
    }

    return format.replace(tokens, token => {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS7053 (33,20): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ d: (date: OwaDate) => number; dd: (date: OwaDate) => string; ddd: (date: OwaDate, s: { ddd: string[]; dddd: string[]; MMM: string[]; MMMM: string[]; standaloneMMMM: string[]; ap: string[]; ampm: string[]; AP: string[]; AMPM: string[]; }) => string; ... 19 more ...; TT: (date: OwaDate, s: { ...; }) => string; }'.
        // @ts-ignore
        const fn = tokenFns[token];
        return fn ? fn(date, locale, strings) : token.slice(1, token.length - 1);
    });
}

type Strings = ReturnType<typeof formatDateStrings>;

const d = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlDate(date, locale, 'numeric');
const dd = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlDate(date, locale, '2-digit');
const ddd = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlDay(date, locale, 'short');
const dddd = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlDay(date, locale, 'long');
const M = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlMonth(date, locale, 'numeric');
const MM = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlMonth(date, locale, '2-digit');
const MMM = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlMonth(date, locale, 'short');
const MMMM = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlMonth(date, locale, 'long');
const yy = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlYear(date, locale, '2-digit');
const yyyy = (date: OwaDate, locale: GlobalCalendarLocale) => getIntlYear(date, locale, 'numeric');
const h = (date: OwaDate) => getHours(date) % 12 || 12;
const hh = pad(h);
const H = getHours;
const HH = pad(H);
const m = getMinutes;
const mm = pad(m);
const s = getSeconds;
const ss = pad(s);
const l = pad(getMilliseconds, 3);
const amPm = (date: OwaDate, s: string[]) => s[getHours(date) < 12 ? 0 : 1];
const t = (date: OwaDate, _locale: GlobalCalendarLocale, s: Strings) => amPm(date, s.AP);
const tt = (date: OwaDate, _locale: GlobalCalendarLocale, s: Strings) => amPm(date, s.AMPM);
const T = (date: OwaDate, _locale: GlobalCalendarLocale, s: Strings) => amPm(date, s.ap);
const TT = (date: OwaDate, _locale: GlobalCalendarLocale, s: Strings) => amPm(date, s.ampm);

function pad(fn: typeof getHours, len: number = 2) {
    return (date: OwaDate) => {
        var str = String(fn(date));
        while (str.length < len) {
            str = '0' + str;
        }
        return str;
    };
}

const tokens = /d{1,4}|M{1,4}|yy(?:yy)?|([hHmstT])\1?|l|"[^"]*"|'[^']*'/g;
const tokenFns = {
    d,
    dd,
    ddd,
    dddd,
    M,
    MM,
    MMM,
    MMMM,
    yy,
    yyyy,
    h,
    hh,
    H,
    HH,
    m,
    mm,
    s,
    ss,
    l,
    t,
    tt,
    T,
    TT,
};
